import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { type Block, BlockType } from '@aws-sdk/client-textract'
import { TranslateService } from '@ngx-translate/core'
import { eq } from 'lodash'

import { CanvasUtils } from '../../canvas-utils'

@Component({
  selector: 'mmx-pdf-text-layer',
  templateUrl: './pdf-text-layer.component.html',
  styleUrls: ['./pdf-text-layer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdfTextLayerComponent implements OnChanges, AfterViewInit {
  @ViewChild('textLayer', { read: ElementRef })
  public canvasRef: ElementRef<HTMLCanvasElement> | undefined

  @Input()
  public blocks: any[] = []

  @Input()
  public page = 1

  private filteredBlocks: Block[] = []
  private hoveredBlock: Block | null = null
  private canvasUtils!: CanvasUtils

  constructor(
    private snackbar: MatSnackBar,
    private translate: TranslateService,
  ) {}

  public ngAfterViewInit() {
    if (!this.canvasRef?.nativeElement) {
      return
    }

    this.canvasUtils = new CanvasUtils(this.canvasRef.nativeElement)
    let mouseMoveTimeout: any

    this.canvasRef.nativeElement.addEventListener('mousemove', (e) => {
      clearTimeout(mouseMoveTimeout)
      mouseMoveTimeout = setTimeout(() => this.onMouseMove(e), 5)
    })
    this.canvasRef.nativeElement.addEventListener('click', () => this.onCanvasClick())

    this.renderBlocks()
  }

  public ngOnChanges({ blocks, page }: SimpleChanges) {
    if (
      eq(blocks?.currentValue, blocks?.previousValue) &&
      page?.currentValue === page?.previousValue
    ) {
      return
    }

    this.renderBlocks()
  }

  private renderBlocks() {
    if (!this.canvasUtils) {
      return
    }

    this.canvasUtils.clearCanvas()
    this.filteredBlocks = []
    this.hoveredBlock = null

    this.blocks.forEach((block) => {
      if (block.BlockType !== BlockType.LINE || !block.Geometry?.BoundingBox || !block.Text) {
        return
      }

      if (block.Page && block.Page !== this.page) {
        return
      }

      const pos = this.canvasUtils.getPosition(block)
      this.filteredBlocks.push(block)
      this.canvasUtils.addRect(pos)
    })
  }

  private onMouseMove(e: MouseEvent) {
    const mousePos = this.canvasUtils.getMousePosition(e)

    /**
     * mouse is still on the same block as before
     * we don't need to do anything
     */
    if (this.hoveredBlock) {
      if (this.canvasUtils.isPointInRect(this.hoveredBlock, mousePos)) {
        return
      }

      /**
       * mouse is no longer on the same block
       * remove the hover effect
       */
      const pos = this.canvasUtils.getPosition(this.hoveredBlock)
      this.canvasUtils.removeRect(pos)
      this.canvasUtils.addRect(pos)
      this.hoveredBlock = null
    }

    // get the block that the mouse is currently on
    const activeBlock = this.filteredBlocks.find((block) => this.canvasUtils.isPointInRect(block, mousePos))

    if (!activeBlock) {
      // Mouse is not on any block
      this.canvasUtils.setCursorStyle('default')
      return
    }

    this.canvasUtils.setCursorStyle('pointer')
    const pos = this.canvasUtils.getPosition(activeBlock)
    this.canvasUtils.removeRect(pos)
    this.canvasUtils.addRect(pos, 'rgba(255, 0, 0, 0.6)')

    this.hoveredBlock = activeBlock
  }

  private onCanvasClick() {
    if (!this.hoveredBlock) {
      return
    }

    this.canvasUtils.copyText(this.hoveredBlock.Text)
    this.snackbar.open(
      this.translate.instant('pdf-reader.status.text-copied', { text: this.hoveredBlock.Text }),
      '',
      { duration: 2000 },
    )
  }
}
